/* eslint-disable */
"use client";
import { useEffect, useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SectionTitle } from "@/components/common/SectionTitle";
import { ReportCard } from "@/components/common/ReportCard";
import { getAuthStatus } from "@/lib/auth";
import { reportService } from "@/services/report.service";
import { curationService } from "@/services/curation.service";

interface ApiReportItem {
  id: number;
  title: string;
  summary?: string;
  createdAt?: string;
  keywords?: string[];
  thumbnailUrl?: string | null;
}

interface PaginatedReportListSectionProps {
  blueTitle?: string;
  blackTitle?: string;
  titleHtml?: string;
  columns: 3 | 4;
  showViewAll?: boolean;
  useCurationPick?: boolean;
  usePersonalized?: boolean;
  useCurationSection?: boolean;  // 큐레이션 섹션용 (1줄 표시)
  items?: ApiReportItem[];
  viewAllHref?: string;
  topicId?: number;  // 토픽 기반 페이징용
}

/**
 * 페이징이 있는 리포트 리스트 섹션
 * 메인 페이지의 큐레이션 섹션 등에서 사용
 */
export function PaginatedReportListSection({ 
  blueTitle = "", 
  blackTitle = "", 
  titleHtml,
  columns, 
  showViewAll = true,
  useCurationPick = false,
  usePersonalized = false,
  useCurationSection = false,
  items: externalItems,
  viewAllHref,
  topicId
}: PaginatedReportListSectionProps) {
  const [header, setHeader] = useState<{ blue: string; black: string }>({ blue: blueTitle, black: blackTitle });
  const [headerHtml, setHeaderHtml] = useState<string>(titleHtml || "");
  const [items, setItems] = useState<ApiReportItem[]>([]);
  const [loading, setLoading] = useState<boolean>(useCurationPick || usePersonalized);
  const [pickTopicId, setPickTopicId] = useState<number | null>(null);
  const [topicPagedItems, setTopicPagedItems] = useState<ApiReportItem[]>([]);
  
  // 페이징 상태
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  // SSR과 클라이언트 렌더링 일치를 위해 초기값은 항상 desktop으로 설정
  const [screenSize, setScreenSize] = useState<'mobile' | 'desktop'>('desktop');
  
  // 반응형 컬럼 수 계산
  const actualColumns = useMemo(() => {
    if (columns === 3) {
      switch (screenSize) {
        case 'mobile': return 1;
        case 'desktop': return 3;
        default: return 3;
      }
    } else if (columns === 4) {
      switch (screenSize) {
        case 'mobile': return 1;
        case 'desktop': return 4;
        default: return 4;
      }
    }
    return columns;
  }, [columns, screenSize]);
  
  // 반응형 itemsPerPage 계산 (2줄 기준)
  const itemsPerPage = useMemo(() => {
    if (usePersonalized) {
      // 개인 맞춤형은 1줄
      return actualColumns;
    } else if (useCurationSection) {
      // 큐레이션 섹션은 모바일에서 1개, 데스크톱에서 1줄
      if (screenSize === 'mobile') {
        return 1; // 모바일에서 강제로 1개씩
      }
      return actualColumns; // 데스크톱에서는 1줄
    } else {
      // 나머지는 2줄
      return actualColumns * 2;
    }
  }, [actualColumns, usePersonalized, useCurationSection, screenSize]);
  
  // 애니메이션 상태
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [isSliding, setIsSliding] = useState<boolean>(false);
  const [isPageTransition, setIsPageTransition] = useState<boolean>(false);
  
  // 터치 스와이프 상태
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);
  const [isSwipeInProgress, setIsSwipeInProgress] = useState<boolean>(false);
  
  // 화면 크기 감지
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    if (width < 1024) { // lg breakpoint - 1024px 이하를 모두 모바일로 인식
      setScreenSize('mobile');
    } else {
      setScreenSize('desktop');
    }
  }, []);
  
  // 화면 크기 감지 이벤트 리스너 - hydration 이후에만 실행
  useEffect(() => {
    // 클라이언트 사이드에서만 화면 크기 확인
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);
  
  // 터치 이벤트 핸들러
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // isPagingEnabled는 나중에 정의되므로 직접 계산
    const pagingEnabled = (
      useCurationPick || 
      usePersonalized || 
      (useCurationSection && Array.isArray(externalItems) && externalItems.length > itemsPerPage) || 
      (topicId && Array.isArray(externalItems) && totalItems > itemsPerPage) ||
      (!topicId && Array.isArray(externalItems) && externalItems.length > itemsPerPage)
    );
    
    if (screenSize !== 'mobile' || !pagingEnabled || isSliding || isPageTransition) return;
    
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
    setTouchEnd(null);
    setIsSwipeInProgress(true);
  }, [screenSize, useCurationPick, usePersonalized, useCurationSection, externalItems, itemsPerPage, topicId, totalItems, isSliding, isPageTransition]);
  
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isSwipeInProgress || !touchStart) return;
    
    const touch = e.touches[0];
    setTouchEnd({ x: touch.clientX, y: touch.clientY });
  }, [isSwipeInProgress, touchStart]);
  
  const handleTouchEnd = useCallback(() => {
    if (!isSwipeInProgress || !touchStart || !touchEnd) {
      setIsSwipeInProgress(false);
      return;
    }
    
    const deltaX = touchEnd.x - touchStart.x;
    const deltaY = touchEnd.y - touchStart.y;
    const minSwipeDistance = 50; // 최소 스와이프 거리
    const maxVerticalDistance = 100; // 수직 스와이프 허용 범위
    
    // 수직 스와이프가 너무 크면 무시 (스크롤과 구분)
    if (Math.abs(deltaY) > maxVerticalDistance) {
      setIsSwipeInProgress(false);
      return;
    }
    
    // 좌우 스와이프 감지
    if (Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX > 0) {
        // 오른쪽으로 스와이프 (이전 페이지)
        goToPreviousPage();
      } else {
        // 왼쪽으로 스와이프 (다음 페이지)
        goToNextPage();
      }
    }
    
    setIsSwipeInProgress(false);
  }, [isSwipeInProgress, touchStart, touchEnd]);
  
  // 화면 크기 변경 시 페이징 상태 초기화
  useEffect(() => {
    setCurrentPage(1);
  }, [actualColumns]);
  
  // 페이징 활성화 여부
  const isPagingEnabled = useMemo(() => {
    return (
      useCurationPick || 
      usePersonalized || // 개인 맞춤형도 페이징 활성화
      (useCurationSection && Array.isArray(externalItems) && externalItems.length > itemsPerPage) || // 큐레이션 섹션은 외부 아이템이 itemsPerPage보다 많을 때만 페이징
      (topicId && Array.isArray(externalItems) && totalItems > itemsPerPage) ||
      (!topicId && Array.isArray(externalItems) && externalItems.length > itemsPerPage)
    );
  }, [useCurationPick, usePersonalized, useCurationSection, topicId, externalItems, totalItems, itemsPerPage]);
  
  // totalPages 계산
  const totalPages = useMemo(() => {
    if (!isPagingEnabled) return 1;
    
    // 큐레이션 섹션 외부 items
    if (useCurationSection && Array.isArray(externalItems) && externalItems.length > 0) {
      return Math.max(1, Math.ceil(externalItems.length / itemsPerPage));
    }
    
    // 토픽 기반 외부 items
    if (topicId && Array.isArray(externalItems)) {
      return Math.max(1, Math.ceil(totalItems / itemsPerPage));
    }
    
    // 일반 외부 items
    if (!topicId && Array.isArray(externalItems) && externalItems.length > 0) {
      return Math.max(1, Math.ceil(externalItems.length / itemsPerPage));
    }
    
    return Math.max(1, Math.ceil(totalItems / itemsPerPage));
  }, [isPagingEnabled, totalItems, itemsPerPage, externalItems, topicId, useCurationSection]);
  
  // 페이지 유효성 체크
  const hasValidDataForPage = (targetPage: number): boolean => {
    if (!isPagingEnabled) return true;
    return targetPage >= 1 && targetPage <= totalPages;
  };
  
  // 페이지 이동 함수
  const goToPreviousPage = () => {
    if (currentPage > 1 && !isSliding && !isPageTransition) {
      const targetPage = currentPage - 1;
      
      if (!hasValidDataForPage(targetPage)) return;
      
      setSlideDirection('right');
      setIsSliding(true);
      setIsPageTransition(true);
      
      // 큐레이션 섹션의 경우 외부 아이템이므로 즉시 페이지 변경
      if (useCurationSection) {
        setTimeout(() => {
          setCurrentPage(targetPage);
        }, 100);
        
        setTimeout(() => {
          setIsSliding(false);
          setSlideDirection(null);
          setIsPageTransition(false);
        }, 700);
      } else {
        setTimeout(() => {
          setCurrentPage(targetPage);
        }, 100);
        
        setTimeout(() => {
          setIsSliding(false);
          setSlideDirection(null);
          setIsPageTransition(false);
        }, 700);
      }
    }
  };
  
  const goToNextPage = () => {
    if (currentPage < totalPages && !isSliding && !isPageTransition) {
      const targetPage = currentPage + 1;
      
      if (!hasValidDataForPage(targetPage)) return;
      
      setSlideDirection('left');
      setIsSliding(true);
      setIsPageTransition(true);
      
      // 큐레이션 섹션의 경우 외부 아이템이므로 즉시 페이지 변경
      if (useCurationSection) {
        setTimeout(() => {
          setCurrentPage(targetPage);
        }, 50);
        
        setTimeout(() => {
          setIsSliding(false);
          setSlideDirection(null);
          setIsPageTransition(false);
        }, 600);
      } else {
        // API 기반 데이터의 경우 데이터 로딩을 먼저 시작하고, 완료 후 애니메이션 시작
        setTimeout(() => {
          setCurrentPage(targetPage);
        }, 50);
        
        setTimeout(() => {
          setIsSliding(false);
          setSlideDirection(null);
          setIsPageTransition(false);
        }, 600);
      }
    }
  };

  // Curation Pick 데이터 로드
  useEffect(() => {
    if (!useCurationPick) return;
    
    if (!isPageTransition) {
      setLoading(true);
    }
    
    const apiPromise = isPagingEnabled 
      ? curationService.getCurationPickPage(currentPage, itemsPerPage)
      : curationService.getCurationPick(itemsPerPage);
      
    apiPromise
      .then((data) => {
        let title = "";
        let reports: ApiReportItem[] = [];
        let topicId: number | null = null;
        let total = 0;
        
        // To-Be 형식 처리
        if (data.code === "SUCCESS" && data.data) {
          const responseData = data.data;
          
          // Type narrowing with 'in' operator
          if ('items' in responseData && Array.isArray(responseData.items)) {
            // PaginationResponse 형식
            reports = responseData.items as unknown as ApiReportItem[];
            total = responseData.total || reports.length;
            // console.log('TITLE :::: ', (responseData as any).title);
            title = (responseData as any).title || "<blue>이달</blue>의 DW'S PICK!";
            topicId = (responseData as any).topicId || null;
          } else if ('reports' in responseData && Array.isArray(responseData.reports)) {
            // CurationPickResponse 형식
            reports = responseData.reports as unknown as ApiReportItem[];
            total = reports.length;
            title = (responseData as any).title || "<blue>이달</blue>의 DW'S PICK!";
            topicId = (responseData as any).topicId || null;
          }
          setItems(reports);
        }
        // Legacy fallback for non-standard responses
        else if (typeof data === 'object' && (data as any)?.total !== undefined) {
          const legacyData = data as any;
          if (!legacyData.error) {
            title = legacyData.title || "<blue>9월</blue>의 DW'S PICK!";
            reports = Array.isArray(legacyData.reports) ? legacyData.reports : [];
            total = legacyData.total;
            topicId = legacyData.topicId;
            setItems(reports);
          }
        } else if ((data as any)?.title && Array.isArray((data as any).reports)) {
          const legacyData = data as any;
          title = legacyData.title;
          reports = legacyData.reports;
          total = legacyData.reports.length;
          topicId = legacyData.topicId;
        } else if (Array.isArray(data)) {
          title = "<blue>9월</blue>의 DW'S PICK!";
          reports = data;
          total = data.length;
        }
        
        if (title) {
          setHeaderHtml(title);
          if (!/<blue>[\s\S]*?<\/blue>/i.test(title)) {
            setHeader({ blue: title, black: "" });
          }
        }
        
        setItems(reports);
        setTotalItems(total);
        
        if (typeof topicId === 'number' && Number.isFinite(topicId)) {
          setPickTopicId(topicId);
        }
        
        if (isPagingEnabled && total > 0) {
          const maxPage = Math.max(1, Math.ceil(total / itemsPerPage));
          if (currentPage > maxPage) {
            setCurrentPage(1);
          }
          
          setTimeout(() => {
            setIsSliding(false);
            setSlideDirection(null);
            setIsPageTransition(false);
          }, 300);
        }
      })
      .catch(() => {
        setItems([]);
        setTotalItems(0);
        
        setTimeout(() => {
          setIsSliding(false);
          setSlideDirection(null);
          setIsPageTransition(false);
        }, 200);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [useCurationPick, isPagingEnabled, currentPage, isPageTransition, itemsPerPage]);

  // Personalized 데이터 로드
  useEffect(() => {
    if (!usePersonalized) return;
    
    getAuthStatus()
      .then((data) => {
        const isLoggedIn = !!(data?.ok && data?.user);
        
        if (!isLoggedIn) {
          setItems([]);
          setTotalItems(0);
          setLoading(false);
          return;
        }
        
        if (!isPageTransition) {
          setLoading(true);
        }
        const limit = isPagingEnabled ? itemsPerPage : 50;
        
        const apiPromise = isPagingEnabled 
          ? reportService.getPersonalizedPage(currentPage, limit)
          : reportService.getPersonalized(1, limit);
          
        apiPromise
          .then((response) => {
            // To-Be 형식 처리
            if (response.code === "SUCCESS" && response.data) {
              const responseData = response.data;
              let reports: any[] = [];
              let total = 0;
              
              // Type narrowing with 'in' operator
              if ('items' in responseData && Array.isArray(responseData.items)) {
                // PaginationResponse 형식
                reports = responseData.items;
                total = responseData.total || reports.length;
              } else if ('reports' in responseData && Array.isArray(responseData.reports)) {
                // PersonalizedResponse 형식
                reports = responseData.reports;
                total = reports.length;
              }
              
              setTotalItems(Number(total));
              setItems(reports);
            } else if (typeof response === 'object' && response !== null && !Array.isArray(response)) {
              const legacyData = response as any;
              const reports = Array.isArray(legacyData.reports) ? legacyData.reports : [];
              setTotalItems(Number(legacyData.total || 0));
              setItems(reports);
            } else if (Array.isArray(response)) {
              setTotalItems(response.length);
              setItems(response);
            }
            
            if (isPagingEnabled && totalItems > 0) {
              const maxPage = Math.max(1, Math.ceil(totalItems / itemsPerPage));
              if (currentPage > maxPage) {
                setCurrentPage(1);
              }
              
              setTimeout(() => {
                setIsSliding(false);
                setSlideDirection(null);
                setIsPageTransition(false);
              }, 300);
            }
          })
          .catch(() => {
            setItems([]);
            setTotalItems(0);
            
            setTimeout(() => {
              setIsSliding(false);
              setSlideDirection(null);
              setIsPageTransition(false);
            }, 200);
          })
          .finally(() => setLoading(false));
      })
      .catch(() => {
        setItems([]);
        setTotalItems(0);
        setLoading(false);
      });
  }, [usePersonalized, currentPage, isPagingEnabled, itemsPerPage, isPageTransition]);

  // 토픽 기반 외부 items 전체 개수 조회
  useEffect(() => {
    if (!topicId || !Array.isArray(externalItems) || useCurationPick || usePersonalized || useCurationSection) return;
    
    curationService.getCurationByTopicPage(Number(topicId), 1, 1)
      .then((response) => {
        // To-Be 형식 처리
        const total = response.code === "SUCCESS" && response.data && 'total' in response.data
          ? response.data.total
          : 0;
        if (total > 0) {
          setTotalItems(total);
        }
      })
      .catch(() => {
        setTotalItems(externalItems.length);
      });
  }, [topicId, externalItems, useCurationPick, usePersonalized, useCurationSection]);

  // 토픽 기반 페이징 데이터 로드
  useEffect(() => {
    if (!topicId || !Array.isArray(externalItems) || useCurationPick || usePersonalized || useCurationSection || !isPagingEnabled) return;
    
    if (currentPage === 1) {
      setTopicPagedItems(externalItems);
      return;
    }
    
    setLoading(true);
    
    curationService.getCurationByTopic(Number(topicId), itemsPerPage)
      .then(response => {
        // To-Be 형식 처리
        const reports = response.code === "SUCCESS" && response.data && Array.isArray(response.data.reports)
          ? response.data.reports
          : [];
        setTopicPagedItems(reports as any);
      })
      .catch(() => {
        setTopicPagedItems([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [topicId, currentPage, externalItems, useCurationPick, usePersonalized, useCurationSection, isPagingEnabled, itemsPerPage]);

  // titleHtml 반영
  useEffect(() => {
    if (typeof titleHtml === 'string' && titleHtml.length > 0) {
      setHeaderHtml(titleHtml);
      if (!/<blue>[\s\S]*?<\/blue>/i.test(titleHtml)) {
        setHeader({ blue: titleHtml, black: "" });
      }
    }
  }, [titleHtml]);

  // 반응형 그리드 컬럼 설정
  const gridCols = columns === 3 
    ? "grid-cols-1 lg:grid-cols-2 lg:grid-cols-3" 
    : "grid-cols-1 lg:grid-cols-2 lg:grid-cols-3 lg:grid-cols-4";
  const cardSize = columns === 3 ? "large" : "medium";
  
  // 반응형 카드 높이 계산
  const cardHeight = useMemo(() => {
    if (usePersonalized || useCurationSection) {
      // 개인 맞춤형과 큐레이션 섹션은 1줄 높이
      if (columns === 3) {
        switch (screenSize) {
          case 'mobile': return "h-auto min-h-[400px]"; // 모바일: 자동 높이, 최소 높이 설정
          case 'desktop': return "h-[640px]"; // 데스크톱: 원래 높이
          default: return "h-[640px]";
        }
      } else {
        switch (screenSize) {
          case 'mobile': return "h-auto min-h-[300px]"; // 모바일: 자동 높이, 최소 높이 설정
          case 'desktop': return "h-[450px]"; // 데스크톱: 원래 높이
          default: return "h-[450px]";
        }
      }
    } else {
      // 나머지는 2줄 높이
      if (columns === 3) {
        switch (screenSize) {
          case 'mobile': return "h-auto min-h-[600px]"; // 모바일: 자동 높이, 최소 높이 설정
          case 'desktop': return "h-[1283px]"; // 데스크톱: 원래 높이
          default: return "h-[1283px]";
        }
      } else {
        switch (screenSize) {
          case 'mobile': return "h-auto min-h-[500px]"; // 모바일: 자동 높이, 최소 높이 설정
          case 'desktop': return "h-[900px]"; // 데스크톱: 원래 높이
          default: return "h-[900px]";
        }
      }
    }
  }, [columns, usePersonalized, useCurationSection, screenSize]);
  
  // 렌더링할 데이터 결정
  const dataToRender: ApiReportItem[] = useMemo(() => {
    // 큐레이션 섹션 외부 items
    if (useCurationSection && Array.isArray(externalItems) && externalItems.length > 0) {
      if (isPagingEnabled) {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return externalItems.slice(startIndex, endIndex);
      }
      return externalItems.slice(0, itemsPerPage);
    }
    
    // 토픽 기반 페이징
    if (topicId && Array.isArray(externalItems) && !useCurationPick && !usePersonalized) {
      if (isPagingEnabled && topicPagedItems.length > 0) {
        return topicPagedItems;
      }
      return externalItems.slice(0, itemsPerPage);
    }
    
    // 일반 외부 items
    if (Array.isArray(externalItems) && externalItems.length > 0) {
      if (isPagingEnabled) {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return externalItems.slice(startIndex, endIndex);
      }
      return externalItems.slice(0, itemsPerPage);
    }
    
    // API 기반 데이터
    if (useCurationPick || usePersonalized) {
      if (isPagingEnabled) {
        return items;
      }
      return items.slice(0, itemsPerPage);
    }
    
    return [];
  }, [externalItems, items, useCurationPick, usePersonalized, useCurationSection, isPagingEnabled, itemsPerPage, currentPage, topicPagedItems, topicId]);

  return (
    <div className={`${cardHeight} content-stretch flex flex-col gap-5 items-center justify-start overflow-clip relative shrink-0 w-full`}>
      {((headerHtml || '').trim().length > 0 || header.blue.trim().length > 0 || header.black.trim().length > 0) && (
        <SectionTitle
          blueText={header.blue}
          blackText={header.black}
          titleHtml={headerHtml}
          showViewAll={showViewAll}
          viewAllHref={
            viewAllHref
              ?? (usePersonalized ? `/reportlist?personal=Y` : (useCurationPick && pickTopicId ? `/reportlist?topic=${pickTopicId}` : undefined))
          }
          viewAllVariant="arrows"
          onPrevious={isPagingEnabled ? goToPreviousPage : undefined}
          onNext={isPagingEnabled ? goToNextPage : undefined}
          canPrevious={isPagingEnabled ? (currentPage > 1 && hasValidDataForPage(currentPage - 1) && !isPageTransition) : false}
          canNext={isPagingEnabled ? (currentPage < totalPages && hasValidDataForPage(currentPage + 1) && !isPageTransition) : false}
        />
      )}
      
      {(loading && !isPageTransition) ? (
        <div className={`flex items-center justify-center w-full ${screenSize === 'mobile' ? 'h-auto min-h-[200px]' : ''}`} style={{ 
          height: screenSize === 'mobile' ? 'auto' : (() => {
            if (usePersonalized || useCurationSection) {
              // 개인 맞춤형과 큐레이션 섹션은 1줄 높이
              if (columns === 3) {
                switch (screenSize) {
                  case 'desktop': return 315; // 데스크톱
                  default: return 315;
                }
              } else {
                switch (screenSize) {
                  case 'desktop': return 230; // 데스크톱
                  default: return 230;
                }
              }
            } else {
              // 나머지는 2줄 높이
              if (columns === 3) {
                switch (screenSize) {
                  case 'desktop': return 630; // 데스크톱
                  default: return 630;
                }
              } else {
                switch (screenSize) {
                  case 'desktop': return 460; // 데스크톱
                  default: return 460;
                }
              }
            }
          })()
        }}>
          <div className="size-8 animate-spin rounded-full border-4 border-[#dadbe0] border-t-[#43464d]" aria-label="로딩 중" />
        </div>
      ) : (
        dataToRender.length > 0 ? (
          <div 
            className={`relative w-full overflow-hidden transition-transform duration-200 ${
              isSwipeInProgress ? 'select-none' : ''
            }`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
              touchAction: screenSize === 'mobile' && (
                useCurationPick || 
                usePersonalized || 
                (useCurationSection && Array.isArray(externalItems) && externalItems.length > itemsPerPage) || 
                (topicId && Array.isArray(externalItems) && totalItems > itemsPerPage) ||
                (!topicId && Array.isArray(externalItems) && externalItems.length > itemsPerPage)
              ) ? (isSwipeInProgress ? 'none' : 'pan-y pinch-zoom') : 'auto'
            }}
          >
            
            <AnimatePresence mode="wait">
              <motion.div
                key={`page-${currentPage}-${useCurationSection ? `curation-${topicId}` : dataToRender.map(item => item.id).join('-')}`}
                initial={{ x: 0, opacity: 1 }}
                animate={{ x: 0, opacity: 1 }}
                exit={slideDirection ? {
                  x: slideDirection === 'left' ? '-100%' : '100%',
                  opacity: 0.7
                } : { x: 0, opacity: 1 }}
                transition={{
                  type: "spring",
                  duration: 0.3,
                  bounce: 0,
                  damping: 30,
                  stiffness: 400
                }}
                className={`box-border ${useCurationSection && screenSize === 'mobile' ? 'gap-4' : 'gap-6 sm:gap-8 lg:gap-6 lg:gap-[30px]'} grid ${gridCols} ${(usePersonalized || useCurationSection) ? 'grid-rows-[repeat(1,_minmax(0px,_1fr))]' : 'grid-rows-[repeat(2,_minmax(0px,_1fr))]'} h-auto overflow-clip pb-5 pt-0 px-0 relative shrink-0 w-full`}
              >
                {dataToRender.map((item, i) => (
                  <motion.div 
                    key={`item-${item.id || `unknown-${i}`}-page-${currentPage}-${useCurationSection ? 'curation' : 'normal'}`} 
                    className={`content-stretch flex flex-col gap-4 sm:gap-6 items-start justify-start relative shrink-0 ${useCurationSection && screenSize === 'mobile' ? 'w-full' : ''}`}
                    initial={{ opacity: 0, y: 15, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                      delay: isPageTransition ? i * 0.03 : i * 0.05,
                      duration: isPageTransition ? 0.2 : 0.3,
                      ease: [0.25, 0.1, 0.25, 1]
                    }}
                  >
                    <ReportCard
                      title={item.title || ""}
                      description={item.summary || ""}
                      date={item.createdAt ? new Date(item.createdAt).toLocaleDateString('ko-KR') : ""}
                      imageUrl={item.thumbnailUrl || "/images/ef1c9ea75bde321990b4893a8c6e39e943b9418c.png"}
                      keywords={item.keywords || ["마케팅"]}
                      size={cardSize as 'large' | 'medium'}
                      href={`/reports/${item.id}`}
                      isCuration={useCurationSection}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
        ) : (
          <div className="flex items-center justify-center w-full h-[200px] text-gray-500">
            데이터가 없습니다.
          </div>
        )
      )}
    </div>
  );
}
